'use client';

import { UserStatus } from '@velocity-exchange/sdk';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import useInterval from 'src/hooks/useInterval';
import { useEffect, useMemo, useRef } from 'react';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useWalletIsConnected from '../useWalletIsConnected';
import { VelocityWindow } from '../../window/velocityWindow';
import {
	AccountLiquidationFlags,
	EMPTY_ACCOUNTS,
	NO_LIQUIDATION_FLAGS,
	useAccountsBeingLiquidatedStore,
} from 'src/stores/useAccountsBeingLiquidatedStore';
import { getSubscribedClient } from 'src/utils/account';

/**
 * `UserAccount.status` is a bitmask and several bits can be set at once — a
 * bankrupt account also carries `BEING_LIQUIDATED`, and `REDUCE_ONLY` can ride
 * along with either. Equality (`matchEnum`) therefore reads a bankrupt account
 * (`status === 3`) as neither bankrupt nor liquidating, so test the bit.
 */
const hasUserStatus = (status: number, flag: UserStatus): boolean =>
	(status & flag) > 0;

/**
 * How long an unreadable sub-account keeps its last known classification.
 *
 * A `User` that momentarily reports unsubscribed (a resubscribe, a socket flap)
 * yields no readable account for a tick or two. Treating that as "not being
 * liquidated" un-gates withdraw/transfer/borrow and lets the liquidation toast
 * tear down and re-fire, so carry the last good answer across the gap instead.
 * Bounded, because an account that never comes back must not stay flagged.
 */
export const UNREADABLE_GRACE_MS = 15_000;

export interface LastGoodLiquidationRead {
	flags: AccountLiquidationFlags;
	readAtMs: number;
}

/**
 * Decides what one sub-account contributes to a scan tick.
 *
 * `read` is `undefined` when the tick could not read the `User` at all. Pure so
 * the grace window can be tested without a React tree.
 *
 * @returns `flags` to publish for this tick (`undefined` publishes nothing), and
 * the `lastGood` entry to carry into the next tick (`undefined` drops it).
 */
export function resolveLiquidationRead(
	read: AccountLiquidationFlags | undefined,
	lastGood: LastGoodLiquidationRead | undefined,
	nowMs: number,
	graceMs: number = UNREADABLE_GRACE_MS
): {
	flags: AccountLiquidationFlags | undefined;
	lastGood: LastGoodLiquidationRead | undefined;
} {
	if (read) {
		return { flags: read, lastGood: { flags: read, readAtMs: nowMs } };
	}

	// Unreadable, and the last good answer has aged out — stop asserting it.
	if (!lastGood || nowMs - lastGood.readAtMs >= graceMs) {
		return { flags: undefined, lastGood: undefined };
	}

	return { flags: lastGood.flags, lastGood };
}

/**
 * Mount ONCE (in `useAppSetup`). Runs the 1s scan across all sub-accounts to
 * determine which are being (or can be) liquidated, and writes the result to
 * `useAccountsBeingLiquidatedStore`. Replaces `singletonHook(...)` so the scan
 * runs exactly once app-wide instead of once per consuming component.
 */
export const useSyncAccountsBeingLiquidated = () => {
	const connected = useWalletIsConnected();
	const userAccounts = useTickedAllSubAccounts();
	const getVelocityAccountStore = useVelocityAccountStore((s) => s.get);
	const setAccountsBeingLiqd = useAccountsBeingLiquidatedStore(
		(s) => s.setAccountsBeingLiquidated
	);
	const setAccountLiquidationFlags = useAccountsBeingLiquidatedStore(
		(s) => s.setAccountLiquidationFlags
	);

	const devEventEmitter = VelocityWindow?.devEventEmitter;

	// Last successful read per sub-account, for bridging an unreadable tick.
	const lastGoodReadRef = useRef(new Map<string, LastGoodLiquidationRead>());

	// Dev mode: force a liquidation alert for the current account
	useEffect(() => {
		if (!devEventEmitter) return;

		const handler = () => {
			const currentAccount =
				getVelocityAccountStore().accounts[
					getVelocityAccountStore().currentUserKey ?? ''
				];
			if (currentAccount) {
				setAccountsBeingLiqd([currentAccount]);
				setAccountLiquidationFlags({
					[currentAccount.userKey]: {
						isBankrupt: false,
						isBeingLiquidated: true,
						isAtRisk: true,
					},
				});
			}
		};

		devEventEmitter.on('force_liquidation_alert', handler);

		return () => {
			devEventEmitter.off('force_liquidation_alert', handler);
		};
	}, [
		devEventEmitter,
		getVelocityAccountStore,
		setAccountsBeingLiqd,
		setAccountLiquidationFlags,
	]);

	useInterval(() => {
		const lastGoodRead = lastGoodReadRef.current;

		if (!connected) {
			lastGoodRead.clear();
			setAccountsBeingLiqd(EMPTY_ACCOUNTS);
			setAccountLiquidationFlags({});
			return;
		}

		const now = Date.now();
		const filteredAccountsBeingLiqd: typeof userAccounts = [];
		const flagsByUserKey: Record<string, AccountLiquidationFlags> = {};
		const seenUserKeys = new Set<string>();

		userAccounts.forEach((account) => {
			seenUserKeys.add(account.userKey);

			// `undefined` means this tick could not read the `User` at all — an
			// unsubscribed or not-yet-loaded client, both of which throw on read.
			let read: AccountLiquidationFlags | undefined;
			try {
				const client = getSubscribedClient(account);
				if (client) {
					const userAccount = client.getUserAccountOrThrow();
					read = {
						isBankrupt: hasUserStatus(userAccount.status, UserStatus.BANKRUPT),
						isBeingLiquidated: hasUserStatus(
							userAccount.status,
							UserStatus.BEING_LIQUIDATED
						),
						isAtRisk: !!client.getLiquidationStatuses().get('cross')
							?.canBeLiquidated,
					};
				}
			} catch (_err) {
				read = undefined;
			}

			const resolved = resolveLiquidationRead(
				read,
				lastGoodRead.get(account.userKey),
				now
			);

			if (resolved.lastGood) {
				lastGoodRead.set(account.userKey, resolved.lastGood);
			} else {
				lastGoodRead.delete(account.userKey);
			}

			if (!resolved.flags) return;

			flagsByUserKey[account.userKey] = resolved.flags;
			if (resolved.flags.isBeingLiquidated) {
				filteredAccountsBeingLiqd.push(account);
			}
		});

		// Sub-accounts that left the list entirely have nothing to carry forward.
		lastGoodRead.forEach((_entry, userKey) => {
			if (!seenUserKeys.has(userKey)) lastGoodRead.delete(userKey);
		});

		setAccountsBeingLiqd(
			filteredAccountsBeingLiqd.length > 0
				? filteredAccountsBeingLiqd
				: EMPTY_ACCOUNTS
		);
		setAccountLiquidationFlags(flagsByUserKey);
	}, 1000);
};

/** Accounts genuinely in `UserStatus.BEING_LIQUIDATED` right now. */
export const useAccountsBeingLiquidated = () =>
	useAccountsBeingLiquidatedStore((s) => s.accountsBeingLiquidated);

/**
 * Cross-margin liquidation flags for a single sub-account, for gating a
 * collateral action (withdraw / borrow / transfer out).
 *
 * `handle_withdraw` and `handle_transfer_deposit` never read the liquidation
 * flag as a blocker — they reject a bankrupt account (`ErrorCode::UserBankrupt`)
 * and otherwise only enforce the *initial* margin requirement, then clear the
 * liquidation flag when that check passes. So `isAtRisk` is a warning, not a
 * block: an account below maintenance has no free collateral at initial margin,
 * which already puts its withdraw limit at zero.
 *
 * Isolated perp statuses are excluded on purpose — they only bear on
 * `withdraw_from_isolated_perp_position`.
 */
export const useAccountLiquidationFlags = (
	userKey?: string | null
): AccountLiquidationFlags =>
	useAccountsBeingLiquidatedStore(
		(s) =>
			(userKey ? s.accountLiquidationFlags[userKey] : undefined) ??
			NO_LIQUIDATION_FLAGS
	);

/**
 * Reads bankruptcy off the flags the scan already wrote rather than re-reading
 * the `User`. Re-reading would drop an account whose client is momentarily
 * unsubscribed, which is exactly the flap the scan's grace window absorbs.
 */
export const useAccountsBeingLiquidatedWithoutBankruptcies = () => {
	const accountsBeingLiquidated = useAccountsBeingLiquidated();
	const flagsByUserKey = useAccountsBeingLiquidatedStore(
		(s) => s.accountLiquidationFlags
	);

	return useMemo(
		() =>
			accountsBeingLiquidated.filter(
				(account) => !flagsByUserKey[account.userKey]?.isBankrupt
			),
		[accountsBeingLiquidated, flagsByUserKey]
	);
};
